import type { CoversheetData } from "@/components/coversheet-document"
import type { CoversheetTemplate } from "./types"

export function fileIcon(name: string): "pdf" | "image" | "other" {
  const ext = name.split(".").pop()?.toLowerCase() ?? ""
  if (["pdf"].includes(ext)) return "pdf"
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext)) return "image"
  return "other"
}

export function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export const ACCEPTED_TYPES = ".pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff,.tif"

export const STEPS = ["Fax Details", "Cover Sheet", "Review & Send"] as const

export function getDefaultCoversheet(): CoversheetData {
  return {
    toName: "",
    toCompany: "",
    toPhone: "",
    toFax: "",
    fromName: "",
    fromCompany: "",
    fromPhone: "",
    fromFax: "",
    date: new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    pages: "1",
    subject: "",
    priority: "review",
    message: "",
    logoUrl: "",
  }
}

/**
 * When a template is loaded, check which fields are still empty and
 * need user input. Subject and message always require input per-fax.
 */
export function getUnfilledTemplateFields(
  tpl: CoversheetTemplate
): { key: string; label: string }[] {
  const checks: { key: keyof CoversheetTemplate["data"]; label: string }[] = [
    { key: "fromName", label: "From Name" },
    { key: "fromCompany", label: "From Company" },
    { key: "fromPhone", label: "From Phone" },
    { key: "fromFax", label: "From Fax" },
    { key: "subject", label: "Subject" },
    { key: "priority", label: "Priority" },
    { key: "message", label: "Message" },
  ]
  return checks.filter((c) => !tpl.data[c.key])
}

/** Returns true if a template has enough data to skip the builder */
export function isTemplateComplete(tpl: CoversheetTemplate): boolean {
  return !!(
    tpl.data.fromName &&
    tpl.data.fromCompany &&
    tpl.data.fromFax &&
    tpl.data.priority
  )
}
